/**
 * TicketEditor - Composer real de respostas e notas internas do ticket
 *
 * Usa shared RichTextEditor (TipTap) pro rich text + `useTicketComposerAttachments`
 * pra staging de anexos com upload real (pending/uploading/completed/failed + retry).
 *
 * Envio: aba "Resposta" -> `useReplyTicket` (`POST /tickets/:id/reply`);
 * aba "Nota Interna" -> `useAddTicketNote` (`POST /tickets/:id/activities`,
 * `isInternal: true`). Ctrl/Cmd+Enter dispara o envio da aba ativa.
 *
 * Imagens podem ir inline no texto (botão imagem do editor, upload direto +
 * `<img>` embutido) OU como anexo em staging (botão clipe) — anexos só
 * entram no payload quando o upload individual termina (`completed`).
 */

import React, { useCallback, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@/shared/components/ui/Button';
import { RichTextEditor } from '@/shared/components/ui/RichTextEditor';
import { Icon } from '@/shared/icons';
import { AttachmentChips } from './composer/AttachmentChips';
import { useTicketComposerAttachments } from '../hooks/useTicketComposerAttachments';
import { useReplyTicket, useAddTicketNote } from '../hooks/useTickets';
import { ticketsService } from '../services/ticketsService';

export interface TicketEditorProps {
  /** ID do ticket dono do composer — os hooks de envio usam esse ID diretamente. */
  ticketId: string;
  /** Título atual do ticket — repassado como contexto pra activity de nota interna. */
  ticketTitle?: string;
  /**
   * Ticket "de e-mail" (`ticket.emailChannelId` presente, Fix I-2) — a aba
   * "Resposta" dispara um e-mail REAL pro contato (`sendReply`, ver
   * `tickets/service.ts`), diferente de tickets comuns onde a resposta é só
   * uma activity interna. Mostra hint + troca o texto do botão só na aba
   * Resposta; Nota Interna permanece inalterada (nunca vira e-mail).
   */
  isEmailTicket?: boolean;
  disabled?: boolean;
}

type EditorTab = 'reply' | 'internal';

const MAX_ATTACHMENTS = 10;

/**
 * HTML "vazio" do TipTap normalmente é `<p></p>` — ignora tags pra checar
 * conteúdo real. Uma imagem inline (sem texto ao redor) conta como conteúdo
 * válido — a tag em si seria removida pelo strip de tags abaixo, então é
 * checada separadamente antes.
 */
const isHtmlContentEmpty = (html: string): boolean => {
  if (/<img[\s/>]/i.test(html)) return false;
  const text = html.replace(/<[^>]*>/g, ' ').replace(/&nbsp;/gi, ' ').trim();
  return text.length === 0;
};

/**
 * Editor de respostas e notas internas do ticket
 */
export const TicketEditor: React.FC<TicketEditorProps> = ({
  ticketId,
  ticketTitle,
  isEmailTicket = false,
  disabled = false
}) => {
  const [activeTab, setActiveTab] = useState<EditorTab>('reply');
  const [content, setContent] = useState('');
  const isInternal = activeTab === 'internal';
  const isEmailReply = isEmailTicket && !isInternal;

  const replyMutation = useReplyTicket();
  const noteMutation = useAddTicketNote();
  const isSending = replyMutation.isPending || noteMutation.isPending;

  const {
    attachments,
    addFiles,
    removeAttachment,
    retryUpload,
    clearAttachments,
    getCompletedAttachments,
    isUploading,
    hasFailedUploads,
  } = useTicketComposerAttachments({
    maxFiles: MAX_ATTACHMENTS,
    onLimitExceeded: (maxAllowed) => {
      toast.error(`Máximo de ${maxAllowed} anexos por mensagem`);
    },
  });

  const contentIsEmpty = useMemo(() => isHtmlContentEmpty(content), [content]);
  const canSubmit = !contentIsEmpty && !disabled && !isSending && !isUploading;

  // Lock SÍNCRONO contra duplo-submit: `isPending` do React Query só muda no
  // próximo re-render, então auto-repeat de Ctrl+Enter (ou duplo clique
  // rápido) passaria pelo guard `isSending` duas vezes. O ref é setado
  // sincronamente antes de disparar a mutação e liberado no finally.
  const submitLockRef = useRef(false);

  const handleContentChange = useCallback((html: string) => {
    setContent(html);
  }, []);

  const handleImageUpload = useCallback(async (file: File): Promise<string> => {
    try {
      const result = await ticketsService.uploadTicketFile(file);
      return result.url;
    } catch (error) {
      toast.error('Erro ao enviar imagem');
      throw error;
    }
  }, []);

  const handleFileAttach = useCallback(
    (files: FileList) => {
      addFiles(files);
    },
    [addFiles]
  );

  const handleSubmit = useCallback(() => {
    if (submitLockRef.current || isSending || disabled || contentIsEmpty) return;
    if (isUploading) {
      toast.error('Aguarde o upload dos anexos terminar');
      return;
    }

    submitLockRef.current = true;
    const attachmentsPayload = getCompletedAttachments();

    void (async () => {
      try {
        if (isInternal) {
          await noteMutation.mutateAsync({
            ticketId,
            body: {
              content,
              ...(ticketTitle !== undefined && { ticketTitle }),
              ...(attachmentsPayload.length > 0 && { attachments: attachmentsPayload }),
            },
          });
          toast.success('Nota interna adicionada!');
        } else {
          await replyMutation.mutateAsync({
            ticketId,
            body: {
              content,
              ...(attachmentsPayload.length > 0 && { attachments: attachmentsPayload }),
            },
          });
          toast.success('Resposta enviada!');
        }
        setContent('');
        clearAttachments();
      } catch {
        toast.error(isInternal ? 'Erro ao adicionar nota interna' : 'Erro ao enviar resposta');
      } finally {
        submitLockRef.current = false;
      }
    })();
  }, [
    isSending,
    disabled,
    contentIsEmpty,
    isUploading,
    getCompletedAttachments,
    isInternal,
    noteMutation,
    replyMutation,
    ticketId,
    content,
    ticketTitle,
    clearAttachments,
  ]);

  return (
    <div
      data-testid="ticket-composer"
      className="rounded-lg border"
      style={{
        backgroundColor: 'var(--theme-card-background)',
        borderColor: 'var(--theme-card-border)'
      }}
    >
      {/* Tabs — padrão pills do Tabs shared (F61): px-3 py-1.5 rounded-md text-sm
          font-medium, ativa = fundo sólido primário + texto branco */}
      <div className="flex items-center gap-1 p-1 px-2.5 pt-2.5 sm:px-3 sm:pt-3">
        <button
          data-testid="ticket-composer-tab-reply"
          onClick={() => setActiveTab('reply')}
          className="flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors"
          style={{
            backgroundColor: activeTab === 'reply' ? 'var(--theme-primary)' : 'transparent',
            color: activeTab === 'reply' ? '#ffffff' : 'var(--theme-muted-text)'
          }}
        >
          <Icon name="message-square" className="w-4 h-4" />
          Resposta
        </button>
        <button
          data-testid="ticket-composer-tab-internal"
          onClick={() => setActiveTab('internal')}
          className="flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors"
          style={{
            backgroundColor: activeTab === 'internal' ? 'var(--theme-primary)' : 'transparent',
            color: activeTab === 'internal' ? '#ffffff' : 'var(--theme-muted-text)'
          }}
        >
          <Icon name="lock" className="w-4 h-4" />
          Nota Interna
        </button>
      </div>

      {/* Info Banner for Internal Notes */}
      {isInternal && (
        <div
          className="flex items-center gap-2 px-4 py-2 text-sm"
          style={{
            backgroundColor: 'rgba(245, 158, 11, 0.10)',
            color: '#b45309'
          }}
        >
          <Icon name="lock" className="w-4 h-4" />
          Nota interna - visível apenas para a equipe
        </div>
      )}

      {/* Info Banner for Email Tickets — só na aba Resposta (Fix I-2) */}
      {isEmailReply && (
        <div
          data-testid="ticket-composer-email-hint"
          className="flex items-center gap-2 px-4 py-2 text-sm"
          style={{
            backgroundColor: 'var(--theme-primary-background)',
            color: 'var(--theme-primary)'
          }}
        >
          <Icon name="mail" className="w-4 h-4" />
          A resposta será enviada por e-mail ao contato
        </div>
      )}

      {/* Rich Text Editor */}
      <RichTextEditor
        value={content}
        onChange={handleContentChange}
        placeholder={
          isInternal
            ? 'Escreva uma nota interna para a equipe...'
            : 'Escreva uma resposta para o cliente...'
        }
        minHeight={120}
        showToolbar={true}
        disabled={disabled || isSending}
        features={{
          bold: true,
          italic: true,
          underline: true,
          textAlign: false,
          bulletList: true,
          orderedList: true,
          heading: false,
          link: true,
          image: true,
          attachment: true,
          blockquote: true,
          code: true,
          codeBlock: true,
        }}
        onImageUpload={handleImageUpload}
        onFileAttach={handleFileAttach}
        onSubmitShortcut={handleSubmit}
        style={{ border: 'none' }}
      />

      {/* Anexos em staging */}
      {attachments.length > 0 && (
        <div className="px-4 pt-3">
          <AttachmentChips
            attachments={attachments}
            onRemove={removeAttachment}
            onRetry={retryUpload}
          />
        </div>
      )}

      {/* Footer — compacto no mobile (#161): rodapé mais justo e o hint
          "Ctrl + Enter" (desktop-only, não serve no touch) some abaixo de sm. */}
      <div
        className="flex items-center justify-between px-3 py-2.5 sm:px-4 sm:py-3 border-t"
        style={{
          borderColor: 'var(--theme-card-border)',
          backgroundColor: 'var(--theme-button-ghost)'
        }}
      >
        <span
          className="text-xs"
          style={{ color: hasFailedUploads ? 'var(--theme-error-color)' : 'var(--theme-muted-text)' }}
        >
          {hasFailedUploads
            ? 'Alguns anexos falharam — tente novamente'
            : <span className="hidden sm:inline">Ctrl + Enter para enviar</span>}
        </span>

        <Button
          data-testid="ticket-composer-submit"
          onClick={handleSubmit}
          variant={isInternal ? 'secondary' : 'primary'}
          size="sm"
          icon={'send'}
          iconPosition="left"
          disabled={!canSubmit}
          loading={isSending}
        >
          {isInternal ? 'Adicionar Nota' : isEmailReply ? 'Enviar E-mail' : 'Enviar Resposta'}
        </Button>
      </div>
    </div>
  );
};
